// 箱单发票(인보이스·패킹리스트) — 창고 출고리스트(엑셀) → 托盘号(파렛트번호)별 COMMERCIAL INVOICE / PACKING LIST 엑셀.
// 순수 함수 모듈(브라우저·node 공용). 화면은 InvoiceGenerator.vue.
//
// 양식은 창고가 실제로 쓰는 箱单发票(2026-10 YZY 샘플) 그대로다: 시트 IV(인보이스)·PA(패킹리스트),
// 머리 블록(수출자/수입자/날짜/항구) 셀 위치·병합·서식·합계 수식까지 맞춘다.
// xlsx(SheetJS CE)는 서식(테두리·글꼴)을 못 쓴다 → OOXML 을 직접 만들고, 압축만 SheetJS 내장 CFB(zip)를 쓴다.
// 새 라이브러리를 들이지 않으려는 것이다.
import * as XLSX from 'xlsx'
import { COL } from './warehouseListParser'
import { packUnitPrices, kgInt, declaredUnitPriceUSD, confirmedNameEn } from './invoicePack'

// ───────────────────────── 출고리스트 → 인보이스 모델 ─────────────────────────

// 출고리스트에서 이 기능만 쓰는 열. 헤더 이름으로 찾고, 없으면 양식 기본 위치(20~22)
const EXTRA = {
  영어품명: { names: ['영어품명'], fallback: 20 },
  HS코드: { names: ['hs코드', 'hscode'], fallback: 21 },
  가격USD: { names: ['가격usd'], fallback: 22 }
}

function text(v) {
  if (v === null || v === undefined) return ''
  return String(v).replace(/\r/g, '').trim()
}
function headerKey(v) {
  return text(v).replace(/[\s/]/g, '').toLowerCase()
}
function findCol(header, def) {
  const h = Array.isArray(header) ? header : []
  for (let c = 0; c < h.length; c++) {
    if (def.names.includes(headerKey(h[c]))) return c
  }
  return def.fallback
}
function num(v) {
  if (typeof v === 'number' && isFinite(v)) return v
  const n = parseFloat(text(v).replace(/[$,\s]/g, ''))
  return isFinite(n) ? n : 0
}
const round = (v, d) => Math.round(v * Math.pow(10, d)) / Math.pow(10, d)

// 영어품명·HS코드·가격USD 열 위치(출고자료 파일도 같은 열을 쓴다 — invoiceRecord.js)
export function extraCols(header) {
  return { en: findCol(header, EXTRA.영어품명), hs: findCol(header, EXTRA.HS코드), price: findCol(header, EXTRA.가격USD) }
}

// 「1.1*1.1*1.19/270KG」「1.1*1.1*126/137kg」 → 치수(m)·무게(kg). 10 넘는 치수는 cm 로 보고 m 로 바꾼다.
// 「1号大箱(原）」처럼 숫자가 없으면 null — 박스형 출고라 무게·부피는 화면에서 넣는다.
export function parsePalletSpec(s) {
  const t = text(s).replace(/[×xX＊]/g, '*')
  const m = t.match(/^\s*([\d.]+)\s*\*\s*([\d.]+)\s*\*\s*([\d.]+)\s*(?:[/／]\s*([\d.]+)\s*kg)?/i)
  if (!m) return null
  const dims = [m[1], m[2], m[3]].map(x => {
    const n = parseFloat(x)
    return n > 10 ? round(n / 100, 3) : n
  })
  return { dims, weightKg: m[4] ? parseFloat(m[4]) : 0, cbm: round(dims[0] * dims[1] * dims[2], 2) }
}

// 散货(托盘 없이 박스로 나가는 묶음) — 托盘号 칸에 「散货」가 적혀 온다
export function isLooseId(id) { return /散货/.test(text(id)) }
// 박스번호 「Z-deareve\nFU092821」 → 箱号 「FU092821」(첫 줄 = 화주 라벨, 箱号 만 남긴다).
// 창고 칸에 \r\n·줄바꿈 여러 개·긴 공백이 섞여 온다(2026-10-07 YZY) → 공백류를 전부 걷고 箱号 꼴(FU092821·SCJ093007)만 모은다.
// 한 칸에 箱号 가 여러 개면 「, 」로 잇는다(화면 箱号 목록과 같은 구분자). 箱号 꼴이 하나도 없으면 마지막 줄을 공백 없이
const BOX_CODE = /^[A-Za-z]{1,4}\d{4,}[A-Za-z0-9-]*$/
export function boxCode(v) {
  const s = v === null || v === undefined ? '' : String(v) // 정규식 공백(\s)이 NBSP·전각 공백까지 잡는다
  const lines = s.split(/\r\n|\r|\n/).map(x => x.replace(/\s+/g, ' ').trim()).filter(Boolean)
  if (!lines.length) return ''
  const codes = lines.join(' ').split(/[\s,，、;；]+/).filter(t => BOX_CODE.test(t))
  return codes.length ? codes.join(', ') : lines[lines.length - 1].replace(/\s+/g, '')
}
// 散货 의 唛头(Mark) — 箱号 범위 「첫 ~ 끝」(BL Marks 와 같은 꼴). 하나면 그 箱号
export function boxMarkText(codes) {
  const list = (codes || []).filter(Boolean)
  if (!list.length) return ''
  return list.length === 1 ? list[0] : list[0] + ' ~ ' + list[list.length - 1]
}
// 箱单发票 의 Mark 칸 값 — 散货 는 箱号, 그 밖은 托盘号(2026-10-04 YZY)
export function palletMark(p) {
  return p && p.kind === 'BOX' && p.boxCodes && p.boxCodes.length ? boxMarkText(p.boxCodes) : (p ? p.id : '')
}

// 箱单发票 의 货主 — 综合清关(통합통관) 행은 COUPASS 명의로 나간다(2026-10-04 YZY). 원래 화주(r.companyid)는 그대로 둔다
export const INTEGRATED_OWNER = 'coupass'
export function ownerOf(r) { return r.invoiceOwner || r.companyid }
// 출고리스트 「清关方式（综合/单独）」 칸(헤더로 찾고 없으면 15번) 값이 통합통관이면 그 행의 货主 를 COUPASS 로 표시한다
export function markIntegratedOwner(aoa, parsed) {
  const header = (aoa && aoa[0]) || []
  const found = header.findIndex(h => /清关方式|通关方式/.test(text(h)))
  const col = found >= 0 ? found : 15
  ;((parsed && parsed.rows) || []).forEach(r => {
    const v = text(((aoa[r.line - 1]) || [])[col])
    if (/统合|综合|통합|integrated/i.test(v)) r.invoiceOwner = INTEGRATED_OWNER
  })
}

// 综合清关 수취인(箱单发票 收货人) — 업로드 칸 「综合清关」 의 전체 행이 이 명의로 나간다. 매번 고르고 첫 값이 기본(2026-10-06 YZY).
// 고정 수취인이 늘면 여기에 한 줄 더한다(id = 营业执照 의 companyid)
export const INTEGRATED_RECIPIENTS = [
  { id: INTEGRATED_OWNER, label: 'COUPASS', file: 'Coupass' }, // file = 일괄 생성 파일명에 쓰는 이름(2026-10-06 YZY 예시 「INVOICE_Coupass-…」)
  { id: 'rubycoco', label: 'rubycoco', file: 'rubycoco' }
]
// 행 전체의 货主 를 한 명의로(综合清关 업로드). co 가 비면 원래 화주(L열)로 되돌린다
export function setOwnerAll(parsed, co) {
  ((parsed && parsed.rows) || []).forEach(r => { if (co) r.invoiceOwner = co; else delete r.invoiceOwner })
}

// 출고리스트 양식 확인 — 0~19번은 위치로 읽으니(COL) 핵심 칸 이름이 아예 다르면 다른 양식이다.
// 글자는 파일마다 조금씩 달라도 된다(「업체」/「업체명」, 「송장번호」/「快递物流单号」). 문제 칸 목록, 없으면 []
const FIXED_CHECK = [
  { c: COL.바코드, want: '바코드번호', ok: /바코드|条码|barcode/i },
  { c: COL.수량, want: '출고수량', ok: /출고/ },
  { c: COL.업체, want: '업체', ok: /업체|货主/ },
  { c: COL.박스번호, want: '박스번호', ok: /박스번호|箱号/ },
  { c: COL.파렛트번호, want: '파렛트번호', ok: /파렛트|托盘/ }
]
export function listHeaderProblems(header) {
  const h = Array.isArray(header) ? header : []
  return FIXED_CHECK.filter(x => !x.ok.test(text(h[x.c]).replace(/\s/g, '')))
    .map(x => ({ col: XLSX.utils.encode_col(x.c), want: x.want, got: text(h[x.c]) }))
}

// 합칠 때 다른 파일엔 있는데 이 파일엔 없는 이름 칸(그 파일 행은 빈 값) — [{ name, cols: ['입고ID', …] }]. 경고만, 합치기는 한다
const NAMED_COLS = [['영어품명', ['영어품명']], ['HS코드', ['hs코드', 'hscode']], ['가격USD', ['가격usd']], ['입고ID', ['입고id']], ['BL번호', ['bl번호']]]
export function listMissingCols(list) {
  const has = x => { const ks = ((x.aoa && x.aoa[0]) || []).map(headerKey); return NAMED_COLS.filter(([, n]) => n.some(k => ks.includes(k))).map(([lbl]) => lbl) }
  const each = list.map(x => ({ name: x.name, has: has(x) }))
  const any = new Set([].concat(...each.map(e => e.has)))
  return each.map(e => ({ name: e.name, cols: [...any].filter(c => !e.has.includes(c)) })).filter(e => e.cols.length)
}

// 같은 양식 출고리스트 여러 개 → 한 표(행은 차례로 이어 붙인다).
// 0~19번 칸은 위치 고정(COL)이라 헤더 글자가 달라도 위치대로 잇는다.
// 20번 이후는 헤더 이름으로 맞춘다 — 창고가 파일마다 칸을 끼워 넣거나(「4-45-56쿠팡건」 X열 출고수량) 빼서(입고ID·BL번호 없음)
// 영어품명·HS코드·가격USD·입고ID·BL번호·중량 위치가 파일마다 다르다(2026-10-07). 이름 없는 칸은 버린다.
// 같은 이름이 한 파일에 두 번이면 n번째끼리 맞춘다. 첫 파일에 없는 이름은 오른쪽에 덧붙인다
export function mergeAoa(list) {
  const FIXED = COL.주문번호 + 1
  const header = ((list[0].aoa && list[0].aoa[0]) || []).slice(0, FIXED)
  while (header.length < FIXED) header.push(null)
  const slot = new Map()
  const maps = list.map(x => {
    const h = (x.aoa && x.aoa[0]) || []
    const seen = {}
    const m = []
    for (let c = FIXED; c < h.length; c++) {
      const k = headerKey(h[c])
      if (!k) continue
      seen[k] = (seen[k] || 0) + 1
      const id = k + '#' + seen[k]
      if (!slot.has(id)) { slot.set(id, header.length); header.push(h[c]) }
      m.push([c, slot.get(id)])
    }
    return m
  })
  const out = [header]
  list.forEach((x, i) => (x.aoa || []).slice(1).forEach(raw => {
    const r = Array.isArray(raw) ? raw : []
    const row = new Array(header.length).fill(null)
    for (let c = 0; c < FIXED; c++) if (r[c] !== undefined) row[c] = r[c]
    maps[i].forEach(([s, t]) => { if (r[s] !== undefined) row[t] = r[s] })
    out.push(row)
  }))
  return out
}

// 托盘号 정렬(2026-10-06 YZY) — 「RUBY0930-01」 의 앞부분(화주·날짜)끼리 묶고, 그 안에서
// 글자 없는 번호(01, 02 …)를 먼저 숫자 순, 글자 있는 번호(P01, KD01 …)를 그 뒤에 글자·숫자 순, 散货 는 맨 뒤
function palletSortKey(id) {
  const s = text(id)
  const cut = s.lastIndexOf('-')
  const head = cut >= 0 ? s.slice(0, cut) : ''
  const tail = cut >= 0 ? s.slice(cut + 1) : s
  const m = tail.match(/^(\D*)(\d*)(.*)$/)
  const group = isLooseId(s) ? 2 : (!m[1] && m[2] ? 0 : 1)
  return { head, group, letters: m[1], n: m[2] ? parseInt(m[2], 10) : Infinity, rest: m[3], s }
}
export function comparePalletId(a, b) {
  const x = palletSortKey(a)
  const y = palletSortKey(b)
  return x.head.localeCompare(y.head) || x.group - y.group || x.letters.localeCompare(y.letters) ||
    (x.n === y.n ? 0 : x.n < y.n ? -1 : 1) || x.rest.localeCompare(y.rest) || x.s.localeCompare(y.s)
}

// 파일 안의 托盘号 목록(파일에 나온 순서). parsed = parseWarehouseList(aoa) 결과
export function listPallets(parsed) {
  const map = new Map()
  for (const r of (parsed && parsed.rows) || []) {
    const id = r.파렛트번호
    if (!id) continue
    if (!map.has(id)) map.set(id, { id, companies: new Set(), origs: new Set(), 행수: 0, 수량: 0, boxes: new Set(), codes: [] })
    const p = map.get(id)
    p.companies.add(ownerOf(r))
    p.origs.add(r.companyid)
    p.행수 += 1
    if (r.수량종류 === 'qty') p.수량 += r.수량
    if (!p.boxes.has(r.박스번호키)) p.codes.push(boxCode(r.박스번호))
    p.boxes.add(r.박스번호키)
  }
  return [...map.values()].map(p => ({
    id: p.id, companyids: [...p.companies], origCompanyids: [...p.origs], 행수: p.행수, 수량: p.수량, 박스수: p.boxes.size,
    loose: isLooseId(p.id), boxCodes: p.codes.filter(Boolean)
  }))
}

/**
 * 고른 托盘号로 인보이스 모델을 만든다.
 * 품목 한 줄 = 파렛트 × (HS코드, 단가). 같은 HS·단가인데 영어품명이 갈리면(오타 등) 수량이 가장 많은 이름을 쓴다.
 * 수량은 숫자 행만 센다 — 「/」 행은 합포장 구성품이라 0 이다.
 * @param {Array<Array>} aoa  sheet_to_json(header:1) 결과
 * @param {object} parsed     parseWarehouseList(aoa) 결과
 * @param {string[]} palletIds
 */
export function buildInvoiceModel(aoa, parsed, palletIds, opts) {
  const header = (aoa && aoa[0]) || []
  const { en: cEn, hs: cHs, price: cPrice } = extraCols(header)
  // 箱单发票 행 순서 = 托盘号 끝 숫자 순(comparePalletId, 2026-10-06 YZY) — 托盘 안 박스 행은 파일 순서 그대로
  const order = listPallets(parsed).map(p => p.id).filter(id => palletIds.includes(id)).sort(comparePalletId)
  const warnings = []
  const companies = new Set()
  const weightOf = makeItemWeight(header) // 행마다 상품 중량을 찾는다(S열 고정 X, 2026-10-07 YZY)
  // 합포장(「10P打包」·구성품 「/」 행)은 申报단가 = 포장 안 각 상품 단가 × N 의 합(invoicePack.js, 2026-10-08 YZY). 일반 건은 가격USD 그대로
  // opts.pack === false = 직원이 이미 단가를 ×N 으로 고쳐 올린 파일(화면 스위치) → 곱하지 않고 파일 값 그대로
  const packPrice = opts && opts.pack === false ? new Map() : packUnitPrices(aoa, parsed, cPrice, COL.작업요청)
  // 고정 申报金额(opts.fixed = 「화주|바코드」→USD 맵, 2026-10-11 YZY)가 있으면 파일 단가·합포장 ×N 대신 그 값을 쓴다(declaredUnitPriceUSD). 없으면 위 계산 그대로
  const fixedMap = opts && opts.fixed
  const declared = (r, raw) => declaredUnitPriceUSD({ companyid: r.companyid, barcode: r.바코드, base: packPrice.has(r.line) ? packPrice.get(r.line).price : num(raw[cPrice]) }, fixedMap)
  const priceOf = (r, raw) => declared(r, raw).price
  // 확정 영문 품명(opts.names = 「화주|바코드」→품명, 2026-10-16 YZY #27) — 있으면 파일의 영어품명 대신 쓴다. 箱单发票·产地证 둘 다 이 모델에서 품명을 읽는다
  const enOf = (r, raw) => confirmedNameEn({ companyid: r.companyid, barcode: r.바코드, base: text(raw[cEn]) }, opts && opts.names)
  const fixedOf = (r, raw) => declared(r, raw).fixed
  const packStat = (r, raw) => (!fixedOf(r, raw || aoa[r.line - 1] || []) && packPrice.has(r.line) ? packPrice.get(r.line) : null) // { status, n } — 화면에 「×N 적용 / 수정본 추정」 표시. 고정금액 행은 ×N 을 안 했으니 표시 없음

  const pallets = order.map(id => {
    const rows = parsed.rows.filter(r => r.파렛트번호 === id)
    const lines = new Map()
    const units = new Set()
    const codes = []
    for (const r of rows) {
      companies.add(ownerOf(r))
      if (!units.has(r.박스번호키)) codes.push(boxCode(r.박스번호))
      units.add(r.박스번호키)
      if (r.수량종류 !== 'qty' || !r.수량) continue
      const raw = aoa[r.line - 1] || []
      const en = enOf(r, raw)
      const hs = text(raw[cHs])
      const price = priceOf(r, raw)
      const fx = fixedOf(r, raw)
      const key = hs + '|' + price + (fx ? '|F' : '')
      if (!lines.has(key)) {
        lines.set(key, { hs, price, qty: 0, names: new Map(), url: '', img: '', 첫행: r.line, pack: '', fixed: fx })
      }
      const l = lines.get(key)
      l.qty += r.수량
      const ps = packStat(r, raw)
      if (ps && ps.status === 'applied') l.pack = '×' + ps.n + 'P'
      else if (ps && ps.status === 'kept' && !l.pack) l.pack = '수정본'
      l.names.set(en, (l.names.get(en) || 0) + r.수량)
      // 창고가 칸을 밀어 적은 행이 있다(URL 칸에 바코드) → http 로 시작하는 값만 쓴다
      const url = text(raw[COL.상품URL])
      const img = text(raw[COL.이미지URL])
      if (!l.url && /^https?:\/\//i.test(url)) l.url = url
      if (!l.img && /^https?:\/\//i.test(img)) l.img = img
      if (!hs || !en || !price) warnings.push(id + ' ' + r.line + '행: 영어품명·HS코드·가격USD 중 빈 칸이 있습니다')
    }
    const items = [...lines.values()].map(l => {
      let desc = ''
      let best = -1
      for (const [n, q] of l.names) if (q > best) { desc = n; best = q }
      if (l.names.size > 1) warnings.push(id + ' HS ' + l.hs + ': 영어품명이 ' + l.names.size + '가지라 「' + desc + '」로 합쳤습니다(' + [...l.names.keys()].join(' / ') + ')')
      return Object.assign({ hs: l.hs, desc, qty: l.qty, price: l.price, amount: round(l.qty * l.price, 4), url: l.url, img: l.img, pack: l.pack }, l.fixed ? { fixed: true } : {})
    })
    const nApplied = items.filter(it => /^×/.test(it.pack)).length
    const nKept = items.filter(it => it.pack === '수정본').length
    if (nApplied) warnings.push(id + ': 合包 申报单价 = 낱개 단가 × N 적용 ' + nApplied + '줄 — 이미 단가를 ×N 으로 고쳐 올린 파일이면 단가가 이중으로 곱해집니다(미리보기 「×NP」 표시 확인)')
    if (nKept) warnings.push(id + ': 직원이 고친 합포장 ' + nKept + '줄(구성품에 수량이 적힌 박스) — 단가를 곱하지 않고 파일 값 그대로 썼습니다')
    // 件数(2026-10-04 YZY): 托盘号에 「散货」가 적힌 건 박스(箱号) 단위, 그 밖의 托盘号는 托盘 하나로 센다.
    // 托盘 안 박스번호가 여러 개여도(Z-deareve FU092801 …) 托盘 1 — 샘플 BL GSPC26064E803A9 = 1托+5箱 = 6 PACKAGES.
    // 托盘 치수/무게는 箱子大小 에 온다(「1.1*1.1*1.19/270KG」) — 그런 값이 있는 첫 행에서 읽는다. 없으면 화면에서 넣는다.
    const isPallet = !isLooseId(id)
    const spec = isPallet ? rows.map(r => parsePalletSpec(r.박스규격)).find(Boolean) : null
    // 散货 는 PA(패킹리스트)에 箱号 하나당 한 줄(2026-10-04 YZY) — 박스별 품목·毛重·体积.
    // 毛重·体积 은 箱子大小 가 「60*40*40/15kg」 꼴일 때 읽힌다. 毛重 은 없으면 상품 중량 × 수량(buildLooseBoxes), 그것도 없으면 0 → 화면에서 넣는다.
    // 비고(실측규격) 칸은 상품 한 개 치수라 박스 값으로 쓰지 않는다.
    const boxes = isPallet ? [] : buildLooseBoxes(rows, aoa, cEn, cHs, priceOf, items, weightOf, packStat, fixedOf, enOf)
    return {
      id,
      kind: isPallet ? 'PLT' : 'BOX',
      origCompanyids: [...new Set(rows.map(r => r.companyid))], // 원래 화주(L열) — 综合清关·화주 합치기 때 货主 와 다르다. 서류 기록에 남는다
      count: isPallet ? 1 : units.size,
      boxCodes: isPallet ? [] : codes.filter(Boolean), // 散货 의 箱号 — Mark 칸에 托盘号 대신 쓴다
      boxes,
      // 托盘 毛重 = 상품 중량 × 출고수량 합(아래 palletWeightKg, 2026-10-06 YZY) — 箱子大小 의 「/270KG」 대신. 散货 는 박스 합 그대로
      gw: isPallet ? palletWeightKg(rows, r => weightOf(aoa[r.line - 1])) : round(boxes.reduce((s, b) => s + b.gw, 0), 2),
      cbm: spec ? spec.cbm : round(boxes.reduce((s, b) => s + b.cbm, 0), 2),
      dims: '', // 尺寸(m) 은 기본 빈칸(2026-10-04 YZY) — 필요한 행만 화면에서 적는다
      items
    }
  })
  if (companies.size > 1) warnings.push('货主(화주)가 여러 명입니다: ' + [...companies].join(', ') + ' — 인보이스는 화주 하나씩 만드세요')
  pallets.forEach(p => { if (!p.items.length) warnings.push(p.id + ': 수량이 있는 행이 없습니다') })
  return { companyids: [...companies], pallets, warnings }
}

// 상품 한 개 중량(kg) — S열(비고 = 실측규격) 끝의 「/무게」: 「21*17*5/0.15KG」「44*16*3/0.15」「43*38*8/0.3k」. g 로 적혔으면 kg 로. 없으면 null
export function itemWeightKg(s) {
  const m = text(s).match(/[/／]\s*(\d+(?:\.\d+)?)\s*(kg|k|g)?[^/／]*$/i)
  const n = m ? parseFloat(m[1]) : 0
  if (!(n > 0)) return null
  return m[2] && m[2].toLowerCase() === 'g' ? n / 1000 : n
}

// 상품 한 개 중량을 행마다 찾는다(2026-10-07 YZY) — 창고가 칸을 밀어 적은 행은 S열에 송장번호가 오고
// 실측규격(「44*16*3/0.15kg」)이 AB·AJ·AK… 다른 칸에 가 있다(택배건 출고리스트 20260930). 찾는 순서:
//   ① 헤더가 중량 칸(重量·毛重·净重·중량·무게·weight·KG)이면 그 값  ② S열 실측규격(itemWeightKg)
//   ③ 그 밖의 칸 중 실측규격 꼴(「치수*치수(*치수)/무게」) 또는 무게만 적힌 칸(「12.5kg」) — 첫 번째 것
// 箱子大小(박스·托盘 무게)·박스번호·托盘号·URL 칸과 托盘 규격(「1.1*1.1*1.19/270KG」)은 상품 중량이 아니라 뺀다.
// 옵션·상품명 같은 글 속의 「1020g左右」 는 칸 전체가 그 꼴이 아니라 안 잡힌다. 못 찾으면 null(= 기존처럼 빈칸)
const WEIGHT_HEADER = /重量|毛重|净重|중량|무게|weight|^kg$|[(（]kg[)）]/i
const SPEC_WEIGHT = /^(\d+(?:\.\d+)?)\s*[*×xX＊]\s*(\d+(?:\.\d+)?)(?:\s*[*×xX＊]\s*\d+(?:\.\d+)?)?\s*[/／]\s*(\d+(?:\.\d+)?)\s*(kg|k|g)?$/i
const ONLY_WEIGHT = /^(\d+(?:\.\d+)?)\s*(kg|公斤|千克|g|克)$/i
function toKg(n, unit) {
  if (!(n > 0)) return null
  return unit && /^(g|克)$/i.test(unit) ? n / 1000 : n
}
// 중량 칸 값 — 숫자면 kg, 글이면 「12.5kg」「500g」「12.5」
function weightCellKg(v) {
  if (typeof v === 'number') return toKg(v)
  const s = text(v).replace(/\s+/g, '')
  const m = s.match(ONLY_WEIGHT) || s.match(/^(\d+(?:\.\d+)?)()$/)
  return m ? toKg(parseFloat(m[1]), m[2]) : itemWeightKg(s)
}
// 아무 칸 — 칸 전체가 실측규격 꼴이거나 무게만 적힌 꼴일 때만
function looseWeightKg(v) {
  if (typeof v !== 'string') return null
  const s = text(v).replace(/\s+/g, ' ')
  const spec = s.match(SPEC_WEIGHT)
  if (spec) {
    if (parseFloat(spec[1]) < 3 && parseFloat(spec[2]) < 3) return null // 托盘 규격(m 단위 1.1*1.1*…)
    return toKg(parseFloat(spec[3]), spec[4])
  }
  const only = s.match(ONLY_WEIGHT)
  return only ? toKg(parseFloat(only[1]), only[2]) : null
}
/** 헤더 → (원본 행 → 상품 한 개 중량 kg | null) */
export function makeItemWeight(header) {
  const h = Array.isArray(header) ? header : []
  const skip = new Set([COL.상품URL, COL.이미지URL, COL.박스규격, COL.박스번호, COL.파렛트번호, COL.실측규격])
  const wCols = []
  h.forEach((v, c) => { if (!skip.has(c) && WEIGHT_HEADER.test(text(v).replace(/\s/g, ''))) wCols.push(c) })
  wCols.forEach(c => skip.add(c))
  return raw => {
    const row = raw || []
    for (const c of wCols) { const w = weightCellKg(row[c]); if (w !== null) return w }
    const s = itemWeightKg(row[COL.실측규격])
    if (s !== null) return s
    for (let c = 0; c < row.length; c++) {
      if (skip.has(c)) continue
      const w = looseWeightKg(row[c])
      if (w !== null) return w
    }
    return null
  }
}
// 托盘 毛重(2026-10-06 YZY) = Σ(상품 중량 × K열 출고수량), 일의 자리를 버려 0 으로 끝나게(157.8 → 150, 1234 → 1230, 10 미만은 정수 — palletKg).
// 수량이 적힌 행 중 하나라도 중량이 없으면 0(= 빈칸, 화면에서 넣는다). 「/」 행(합포장 구성품)은 수량 0 이라 안 센다.
// weightOf(r) = 그 행의 상품 중량(makeItemWeight). 안 주면 S열만 본다
export function palletWeightKg(rows, weightOf) {
  const wOf = weightOf || (r => itemWeightKg(r.실측규격))
  let sum = 0
  for (const r of rows || []) {
    if (r.수량종류 !== 'qty' || !r.수량) continue
    const w = wOf(r)
    if (w === null) return 0
    sum += w * r.수량
  }
  return palletKg(sum)
}

// 托盘 毛重 끝수 규칙(2026-10-07 YZY) — 箱单发票·产地证·서류 기록이 모두 이 값(p.gw)을 쓴다.
// 10 이상 = 일의 자리 버림(237.6 → 230). 10 미만 = 소수만 버린 정수(9.6 → 9), 0 이 되면 1(0 = 모름·빈칸이라서)
export function palletKg(kg) {
  const v = round(num(kg), 6)
  if (!(v > 0)) return 0
  if (v >= 10) return Math.floor(v / 10) * 10
  return Math.max(1, Math.floor(v))
}
// 세트 총중량 → 托盘 별 毛重(2026-10-07 YZY): 기준값 비율로 나눠 palletKg 로 내리고,
// 모자란 만큼 10 씩 앞 托盘부터 돌려 더하고, 10 미만 나머지는 첫 托盘에 더한다 → 합 = 총중량 정확히(소수 둘째 자리).
// 기준이 전부 0 이면 똑같이 나눈다
export function splitPalletKg(total, bases) {
  const n = bases.length
  if (!n) return []
  const t = round(Math.max(0, num(total)), 2)
  const w = bases.map(b => Math.max(0, num(b)))
  const sum = w.reduce((s, x) => s + x, 0)
  const parts = w.map(x => palletKg(sum ? (x / sum) * t : t / n))
  let rest = Math.round((t - parts.reduce((s, x) => s + x, 0)) * 100) // 0.01kg 단위
  if (rest < 0) { // 10 미만 托盘이 1 로 올라가 넘친 경우 — 첫 托盘부터 덜어낸다(드묾)
    for (let i = 0; i < n && rest < 0; i++) { const d = Math.min(parts[i] * 100, -rest); parts[i] = round(parts[i] - d / 100, 2); rest += d }
    return parts
  }
  for (let i = 0; rest >= 1000; i = (i + 1) % n) { parts[i] += 10; rest -= 1000 }
  parts[0] = round(parts[0] + rest / 100, 2)
  return parts
}

// 散货 의 박스별 줄 — 박스(箱号) 순서대로, 박스 안은 (HS코드, 단가) 한 줄. 품명은 托盘 단위로 고른 이름(items)과 같게.
// 박스 毛重 = 箱子大小 의 「/15kg」, 없으면 Σ(상품 중량 × 수량)(2026-10-07 YZY) — 수량 행 중 하나라도 중량이 없으면 0(화면에서 넣는다)
function buildLooseBoxes(rows, aoa, cEn, cHs, priceOf, items, weightOf, packStat, fixedOf, enOf) {
  const descOf = new Map(items.map(it => [it.hs + '|' + it.price + (it.fixed ? '|F' : ''), it.desc]))
  const map = new Map()
  for (const r of rows) {
    if (!map.has(r.박스번호키)) {
      const spec = parsePalletSpec(r.박스규격)
      map.set(r.박스번호키, { code: boxCode(r.박스번호), co: ownerOf(r), lines: new Map(), gw: spec ? kgInt(spec.weightKg) : 0, cbm: spec ? spec.cbm : 0, dims: '', itemKg: 0, itemKgOk: true })
    }
    if (r.수량종류 !== 'qty' || !r.수량) continue
    const w = weightOf ? weightOf(aoa[r.line - 1]) : null
    const bx = map.get(r.박스번호키)
    if (w === null) bx.itemKgOk = false
    else bx.itemKg += w * r.수량
    const raw = aoa[r.line - 1] || []
    const hs = text(raw[cHs])
    const price = priceOf(r, raw)
    const fx = fixedOf ? fixedOf(r, raw) : false
    const key = hs + '|' + price + (fx ? '|F' : '')
    const b = map.get(r.박스번호키)
    if (!b.lines.has(key)) b.lines.set(key, Object.assign({ hs, desc: descOf.get(key) || (enOf ? enOf(r, raw) : text(raw[cEn])), qty: 0, price, pack: '' }, fx ? { fixed: true } : {}))
    b.lines.get(key).qty += r.수량
    const ps = packStat(r, raw)
    if (ps && ps.status === 'applied') b.lines.get(key).pack = '×' + ps.n + 'P'
    else if (ps && ps.status === 'kept' && !b.lines.get(key).pack) b.lines.get(key).pack = '수정본'
  }
  return [...map.values()].filter(b => b.code).map(b => ({
    code: b.code, co: b.co, gw: b.gw || (b.itemKgOk && b.lines.size ? kgInt(b.itemKg) : 0), cbm: b.cbm, dims: b.dims,
    items: [...b.lines.values()].map(l => Object.assign(l, { amount: round(l.qty * l.price, 4) }))
  }))
}
// 박스별 값이 있는 散货 인가 — PA 를 박스 줄로 펼친다
export function hasBoxRows(p) {
  return !!(p && p.kind === 'BOX' && Array.isArray(p.boxes) && p.boxes.length && p.boxes.some(b => b.items && b.items.length))
}
// 散货 托盘 의 毛重 = 박스 합(화면에서 박스 값을 고치면 다시 맞춘다).
// 体积 은 散货 묶음 하나에 한 값(方数计划 총 방수, 2026-10-04 YZY) — 박스로 나누지 않고 p.cbm 을 그대로 쓴다
export function syncBoxTotals(p) {
  if (!hasBoxRows(p)) return
  p.gw = round(p.boxes.reduce((s, b) => s + num(b.gw), 0), 2)
}

// ───────────────────────── 부피·중량 수동 수정(2026-10-07 YZY) ─────────────────────────
// 자동값(출고리스트·方数计划) 위에 서류에만 덮는다 — 원본 출고리스트는 안 건드린다.
// 수정값 e = { gw, cbm, boxes: { 箱号: 毛重 } }(넣은 칸만). 散货 毛重 은 박스 단위로만 갖는다(托盘 합계 = 박스 합).
// p.auto·b.autoGw = 자동값, p.volEdit·b.edited = 고친 칸 — 서류 기록(pallets)에 같이 남아 목록에서 다시 받아도·다시 만들어도 유지된다
// 자동값을 잡아 둔다 — 方数计划 적용 뒤에 부른다
export function markAutoVolume(p) {
  p.auto = { gw: num(p.gw), cbm: num(p.cbm) }
  ;(p.boxes || []).forEach(b => { b.autoGw = num(b.gw) })
}
// 자동값으로 되돌린다(markAutoVolume 을 부른 적이 있을 때)
export function restoreAutoVolume(p) {
  if (!p.auto) return
  p.gw = p.auto.gw
  p.cbm = p.auto.cbm
  ;(p.boxes || []).forEach(b => { if (b.autoGw !== undefined) b.gw = b.autoGw })
}
const isVal = v => v !== null && v !== undefined && v !== '' && isFinite(Number(v)) && Number(v) >= 0
// 자동값 + 수정값 → 덮어쓴 값(바꾸지 않고 계산만) — { gw, cbm, boxGw: [박스 순서], edit: { gw, cbm } }
export function volumeWithEdit(p, e) {
  const auto = p.auto || { gw: num(p.gw), cbm: num(p.cbm) }
  const edit = { gw: false, cbm: false }
  let cbm = auto.cbm
  if (e && isVal(e.cbm)) { cbm = round(Number(e.cbm), 2); edit.cbm = true }
  let gw = auto.gw
  let boxGw = null
  if (hasBoxRows(p)) {
    const bx = (e && e.boxes) || {}
    boxGw = p.boxes.map(b => {
      const a = b.autoGw !== undefined ? b.autoGw : num(b.gw)
      if (!isVal(bx[b.code])) return a
      edit.gw = true
      return round(Number(bx[b.code]), 2)
    })
    gw = round(boxGw.reduce((s, v) => s + num(v), 0), 2)
  } else if (e && isVal(e.gw)) { gw = round(Number(e.gw), 2); edit.gw = true }
  return { gw, cbm, boxGw, edit }
}
// 수정값을 托盘 에 덮는다(없으면 자동값으로). markAutoVolume 뒤에 부른다
export function applyVolumeEdit(p, e) {
  if (!p.auto) markAutoVolume(p)
  const v = volumeWithEdit(p, e)
  p.gw = v.gw
  p.cbm = v.cbm
  p.volEdit = v.edit.gw || v.edit.cbm ? v.edit : null
  if (v.boxGw) {
    const bx = (e && e.boxes) || {}
    p.boxes.forEach((b, i) => { b.gw = v.boxGw[i]; b.edited = isVal(bx[b.code]) })
  }
}
// 합계를 기준값 비율로 나눈다(소수 둘째 자리, 나눈 합 = 합계 정확히). 기준이 전부 0 이면 똑같이 나눈다 — 세트 합계 → 托盘, 托盘 毛重 → 散货 박스
export function splitTotal(total, bases) {
  const n = bases.length
  if (!n) return []
  const t = Math.round(num(total) * 100)
  const w = bases.map(b => Math.max(0, num(b)))
  const sum = w.reduce((s, x) => s + x, 0)
  const raw = w.map(x => (sum ? (x / sum) * t : t / n))
  const parts = raw.map(Math.floor)
  let rest = t - parts.reduce((s, x) => s + x, 0)
  raw.map((x, i) => [x - parts[i], i]).sort((a, b) => b[0] - a[0]).forEach(([, i]) => { if (rest > 0) { parts[i] += 1; rest -= 1 } })
  return parts.map(x => x / 100)
}

// ───────────────────────── 方数计划(창고 부피 계획표) ─────────────────────────
// 샘플(2026.09.30方数计划.xlsx, 2026-10-04 YZY): 열 = 托盘号 · 尺寸(「1.1*1.1*2.1」 m) · 方数(CBM), 托盘号 한 줄씩.
// 散货 는 托盘号 칸에 「…散货」, 尺寸 에 「1号4个，2号1个」(박스 종류·개수 — 치수가 아니다), 方数 = 그 散货 전체.
// 열은 헤더 이름으로 찾고 없으면 0·1·2. 맨 끝 합계 줄(托盘号 빈칸)은 건너뛴다.
// 굵은 테두리 묶음(2026-10-06 YZY): 굵은 선으로 둘러싼 칸 하나 = 综合清关 箱单发票+产地证 한 세트.
// readPlanBreaks 의 경계 줄을 주면 행마다 block(0부터)을 붙인다. 경계를 못 찾으면 block 없음
export const planKey = id => text(id).replace(/\s/g, '').toUpperCase()
export function parseVolumePlan(aoa, breaks) {
  const header = (aoa && aoa[0]) || []
  const find = (names, fb) => { const i = header.findIndex(h => names.includes(text(h).replace(/\s/g, ''))); return i >= 0 ? i : fb }
  const cId = find(['托盘号', '托盘', '파렛트번호'], 0)
  const cDims = find(['尺寸', '尺寸(m)', '사이즈'], 1)
  const cCbm = find(['方数', '方数(CBM)', 'CBM', '체적'], 2)
  const brk = breaks && breaks.size ? breaks : null
  let block = 0
  let inBlock = 0
  const map = new Map()
  ;(aoa || []).forEach((r, i) => {
    if (i === 0) return
    if (brk && brk.has(i + 1) && inBlock) { block += 1; inBlock = 0 } // aoa i 번째 = 엑셀 i+1 행
    const id = planKey(r && r[cId])
    if (!id) return
    const dimsText = text(r[cDims])
    const v = {
      dims: /^\s*[\d.]+\s*[*×xX]\s*[\d.]+\s*[*×xX]\s*[\d.]+\s*$/.test(dimsText) ? dimsText.replace(/\s/g, '').replace(/[×xX]/g, '*') : '',
      cbm: num(r[cCbm])
    }
    if (brk) { v.block = block; inBlock += 1 }
    map.set(id, v)
  })
  return map
}

// 方数计划 xlsx 의 굵은 가로선 위치 → 경계 줄 번호 Set(r 이 있으면 r-1 행과 r 행 사이에 굵은 선).
// SheetJS 무료판은 테두리를 못 읽어서 zip 안의 styles.xml·첫 시트 xml 을 직접 본다:
// 셀 s(서식 번호) → cellXfs 의 borderId → borders 의 top/bottom 선 굵기
const BOLD_LINE = /^(medium|thick|double|mediumDashed|mediumDashDot|mediumDashDotDot|slantDashDot)$/
export function readPlanBreaks(u8) {
  const cfb = XLSX.CFB.read(u8, { type: 'array' })
  const dec = new TextDecoder()
  const get = p => { const e = XLSX.CFB.find(cfb, '/' + p); return e && e.content ? dec.decode(e.content) : '' }
  // 첫 시트 경로 — workbook.xml 첫 <sheet> 의 r:id → workbook.xml.rels 의 Target
  const rid = (get('xl/workbook.xml').match(/<sheet\b[^>]*\br:id="([^"]+)"/) || [])[1]
  const rel = rid ? (get('xl/_rels/workbook.xml.rels').match(new RegExp('<Relationship\\b[^>]*\\bId="' + rid + '"[^>]*>')) || [])[0] : ''
  const target = ((rel || '').match(/\bTarget="([^"]+)"/) || [])[1] || 'worksheets/sheet1.xml'
  const sheet = get(target.replace(/^\/?(xl\/)?/, 'xl/'))
  const styles = get('xl/styles.xml')
  const side = (b, tag) => BOLD_LINE.test((b.match(new RegExp('<' + tag + '\\b[^>]*\\bstyle="([^"]+)"')) || [])[1] || '')
  const borders = (((styles.match(/<borders\b[\s\S]*?<\/borders>/) || [''])[0]).match(/<border\b[^>]*\/>|<border\b[^>]*>[\s\S]*?<\/border>/g) || [])
    .map(b => ({ top: side(b, 'top'), bottom: side(b, 'bottom') }))
  const xfs = (((styles.match(/<cellXfs\b[\s\S]*?<\/cellXfs>/) || [''])[0]).match(/<xf\b[^>]*>/g) || [])
    .map(x => Number((x.match(/\bborderId="(\d+)"/) || [])[1] || 0))
  const out = new Set()
  ;(sheet.match(/<c\b[^>]*>/g) || []).forEach(tag => {
    const r = Number((tag.match(/\br="[A-Z]+(\d+)"/) || [])[1] || 0)
    const b = borders[xfs[Number((tag.match(/\bs="(\d+)"/) || [])[1] || 0)]]
    if (!r || !b) return
    if (b.top) out.add(r)
    if (b.bottom) out.add(r + 1)
  })
  return out
}

/**
 * 综合清关 세트 나누기 — 方数计划 굵은 테두리 묶음(block) 하나 = 한 세트. 묶음 순서대로.
 * 계획표에 없는 托盘号 는 맨 뒤 한 세트(missing:true)로 모은다(빠뜨리지 않으려고).
 * @returns {Array<{block:number|null, ids:string[], missing?:boolean}>}  ids 는 comparePalletId 순
 */
export function splitByPlanBlocks(palletIds, plan) {
  const map = new Map()
  const missing = []
  palletIds.forEach(id => {
    const v = plan && plan.get(planKey(id))
    if (!v || v.block === undefined) { missing.push(id); return }
    if (!map.has(v.block)) map.set(v.block, [])
    map.get(v.block).push(id)
  })
  const sets = [...map.keys()].sort((a, b) => a - b).map(b => ({ block: b, ids: map.get(b).sort(comparePalletId) }))
  if (missing.length) sets.push({ block: null, ids: missing.sort(comparePalletId), missing: true })
  return sets
}
// 方数计划 에 묶음 정보가 있나(이 기능 전에 올린 계획표엔 없다 → 다시 올려야 한다)
export function planHasBlocks(plan) {
  return !!plan && [...plan.values()].some(v => v.block !== undefined)
}

/**
 * 单独清关 세트 나누기 — 출고리스트 L열(업체 = companyid) 화주 한 명 = 한 세트.
 * 화주별로 행을 떼어 낸 parsed 를 같이 준다(한 托盘 에 화주가 섞여도 남의 품목이 안 들어가게).
 */
// 单独清关 인데 COUPASS 명의로 나가는 화주(2026-10-07 YZY) — 창고 표 아무 칸(P열 清关方式·M열 요청사항 등)에
// 아래 글자가 있으면(공백·대소문자 무시) 그 화주의 营业执照 를 COUPASS 로 자동 매칭한다. 화주단독 건은 그대로
export const COUPASS_SEPARATE_KEYWORDS = ['coupass单独清关', 'coupass单独通关', 'coupass單獨清關', 'coupass單獨通關', '쿠패스단독통관']
// aoa = 합친 표, parsed = parseWarehouseList(aoa) → 키워드가 나온 화주(companyid) Set
export function coupassSeparateOwners(aoa, parsed) {
  const out = new Set()
  ;((parsed && parsed.rows) || []).forEach(r => {
    if (out.has(ownerOf(r))) return
    const raw = (aoa && aoa[r.line - 1]) || []
    const hit = raw.some(v => {
      const s = text(v).replace(/\s/g, '').toLowerCase()
      return !!s && COUPASS_SEPARATE_KEYWORDS.some(k => s.includes(k))
    })
    if (hit) out.add(ownerOf(r))
  })
  return out
}
// 화주별 세트 — 货主 = ownerOf(r). 单独清关 에서 여러 화주를 한 세트로 합쳤으면(r.invoiceOwner = 대표 화주, 2026-10-07 YZY)
// 대표 화주 한 세트로 묶이고 members = 원래 화주들(L열)
export function splitByOwner(parsed) {
  const owners = [...new Set(((parsed && parsed.rows) || []).map(ownerOf))].sort((a, b) => a.localeCompare(b))
  return owners.map(co => {
    const rows = parsed.rows.filter(r => ownerOf(r) === co)
    const members = [...new Set(rows.map(r => r.companyid))].sort((a, b) => a.localeCompare(b))
    const sub = Object.assign({}, parsed, { rows, groups: (parsed.groups || []).filter(g => members.includes(g.companyid)) })
    return { co, members, parsed: sub, ids: listPallets(sub).map(p => p.id).sort(comparePalletId) }
  })
}
// 托盘 하나에 方数计划 적용 — 托盘 은 尺寸·方数 그대로, 散货 는 方数 를 묶음 전체 한 값으로
// (계획표엔 散货 전체 方数 만 있다 — 박스로 나누지 않고 미리보기·PA 에서 箱号 줄을 합친 한 칸에 쓴다, 2026-10-04 YZY). 없으면 false
export function applyVolumePlan(p, plan) {
  const v = plan && plan.get(planKey(p.id))
  if (!v) return false
  p.cbm = round(v.cbm, 2)
  if (v.dims && !hasBoxRows(p)) p.dims = v.dims
  return true
}

// 포장 수 표기 — 「1PLT」, 「25BOX」, 섞이면 「2PLT+5BOX」
export function packageCountText(pallets) {
  const plt = pallets.filter(p => p.kind === 'PLT').reduce((s, p) => s + p.count, 0)
  const box = pallets.filter(p => p.kind === 'BOX').reduce((s, p) => s + p.count, 0)
  return [plt ? plt + 'PLT' : '', box ? box + 'BOX' : ''].filter(Boolean).join('+') || '0'
}

// 파일명 — INVOICE_업체_(화주)_날짜-N（托盘号）.xlsx
export function invoiceFileName(info, model) {
  const clean = s => text(s).replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim()
  const d = text(info.date).replace(/-/g, '').slice(4) // MMDD
  const ids = model.pallets.map(p => p.id).join('、')
  const co = model.companyids.join('+')
  return clean('INVOICE_' + (info.importer.name || co) + '_(' + co + ')_' + d + '-' + model.pallets.length + '（' + ids + '）') + '.xlsx'
}

// 일괄 생성 서류 이름(확장자 없이, 2026-10-06 YZY) — 「INVOICE_Coupass-20260930-1(RUBY0930-01~RUBY0930-24)」 의 INVOICE_ 뒤 부분.
// who = 수취인(综合)·화주(单独), planDate = 方数计划 날짜(YYYY-MM-DD), seq = 方数计划 굵은 테두리 순번(单独 은 综合 다음부터), ids = 그 세트 托盘号(정렬된)
export function batchDocBase(who, planDate, seq, ids) {
  const clean = s => text(s).replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim()
  const range = ids.length ? '(' + ids[0] + (ids.length > 1 ? '~' + ids[ids.length - 1] : '') + ')' : ''
  return clean(who + '-' + text(planDate).replace(/-/g, '') + '-' + seq + range)
}

// ───────────────────────── OOXML 작성 ─────────────────────────

const FONT = '微软雅黑'
const EMU_PER_PX = 9525

function colName(i) {
  let s = ''
  i += 1
  while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26) }
  return s
}
function decode(addr) {
  const m = /^([A-Z]+)(\d+)$/.exec(addr)
  let c = 0
  for (const ch of m[1]) c = c * 26 + (ch.charCodeAt(0) - 64)
  return { c: c - 1, r: parseInt(m[2], 10) - 1 }
}
function esc(s) {
  // eslint-disable-next-line no-control-regex
  return String(s).replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
function toSerial(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text(ymd))
  if (!m) return null
  return (Date.UTC(+m[1], +m[2] - 1, +m[3]) - Date.UTC(1899, 11, 30)) / 86400000
}

// 서식 등록소 — 셀 서식 조합마다 cellXfs 하나
export class Styles {
  constructor() {
    this.fonts = [`<font><sz val="11"/><name val="${FONT}"/><charset val="134"/></font>`]
    this.borders = ['<border><left/><right/><top/><bottom/><diagonal/></border>']
    this.numFmts = []
    this.xfs = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>']
    this.fills = ['<fill><patternFill patternType="none"/></fill>', '<fill><patternFill patternType="gray125"/></fill>']
    this.keys = { font: {}, border: {}, fmt: {}, xf: {}, fill: {} }
  }
  _font(f) {
    const k = JSON.stringify(f)
    if (this.keys.font[k] === undefined) {
      this.keys.font[k] = this.fonts.length
      this.fonts.push('<font>' + (f.b ? '<b/>' : '') + `<sz val="${f.sz || 11}"/>` + (f.color ? `<color rgb="${f.color}"/>` : '') +
        `<name val="${esc(f.name || FONT)}"/><charset val="134"/></font>`)
    }
    return this.keys.font[k]
  }
  _border(b) {
    const k = [...new Set(b.split(''))].sort().join('')
    if (!k) return 0
    if (this.keys.border[k] === undefined) {
      this.keys.border[k] = this.borders.length
      const side = (tag, on) => on ? `<${tag} style="thin"><color auto="1"/></${tag}>` : `<${tag}/>`
      this.borders.push('<border>' + side('left', k.includes('L')) + side('right', k.includes('R')) +
        side('top', k.includes('T')) + side('bottom', k.includes('B')) + '<diagonal/></border>')
    }
    return this.keys.border[k]
  }
  _fmt(code) {
    if (!code) return 0
    if (code === '@') return 49
    if (this.keys.fmt[code] === undefined) {
      const id = 164 + this.numFmts.length
      this.keys.fmt[code] = id
      this.numFmts.push(`<numFmt numFmtId="${id}" formatCode="${esc(code)}"/>`)
    }
    return this.keys.fmt[code]
  }
  // 배경색(rgb 'FFF8CBAD') — 없으면 0(채우기 없음)
  _fill(rgb) {
    if (!rgb) return 0
    if (this.keys.fill[rgb] === undefined) {
      this.keys.fill[rgb] = this.fills.length
      this.fills.push(`<fill><patternFill patternType="solid"><fgColor rgb="${esc(rgb)}"/><bgColor indexed="64"/></patternFill></fill>`)
    }
    return this.keys.fill[rgb]
  }
  id(s) {
    const st = s || {}
    const fontId = this._font(st.font || {})
    const borderId = this._border(st.border || '')
    const fmtId = this._fmt(st.fmt)
    const fillId = this._fill(st.fill)
    const al = (st.h || st.v || st.wrap) ? `<alignment${st.h ? ` horizontal="${st.h}"` : ''}${st.v ? ` vertical="${st.v}"` : ''}${st.wrap ? ' wrapText="1"' : ''}/>` : ''
    const k = [fontId, borderId, fmtId, al, fillId].join('|')
    if (this.keys.xf[k] === undefined) {
      this.keys.xf[k] = this.xfs.length
      this.xfs.push(`<xf numFmtId="${fmtId}" fontId="${fontId}" fillId="${fillId}" borderId="${borderId}" xfId="0"` +
        (fmtId ? ' applyNumberFormat="1"' : '') + ' applyFont="1"' + (fillId ? ' applyFill="1"' : '') + (borderId ? ' applyBorder="1"' : '') +
        (al ? ' applyAlignment="1">' + al + '</xf>' : '/>'))
    }
    return this.keys.xf[k]
  }
  xml() {
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
      (this.numFmts.length ? `<numFmts count="${this.numFmts.length}">${this.numFmts.join('')}</numFmts>` : '') +
      `<fonts count="${this.fonts.length}">${this.fonts.join('')}</fonts>` +
      `<fills count="${this.fills.length}">${this.fills.join('')}</fills>` +
      `<borders count="${this.borders.length}">${this.borders.join('')}</borders>` +
      '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
      `<cellXfs count="${this.xfs.length}">${this.xfs.join('')}</cellXfs>` +
      '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
      '</styleSheet>'
  }
}

// 시트 한 장 — 셀 값·서식·병합·테두리·열 너비·행 높이·그림
export class Sheet {
  constructor(name) {
    this.name = name
    this.cells = {}
    this.merges = []
    this.cols = {}
    this.heights = {}
    this.images = []
  }
  cell(addr) {
    if (!this.cells[addr]) this.cells[addr] = { s: {} }
    return this.cells[addr]
  }
  put(addr, value, style) {
    const c = this.cell(addr)
    if (value !== undefined && value !== null && value !== '') c.v = value
    if (style) c.s = Object.assign({}, c.s, style, { border: (c.s.border || '') + ((style && style.border) || '') })
    return this
  }
  formula(addr, f, cached, style) {
    const c = this.cell(addr)
    c.f = f
    c.v = cached
    if (style) c.s = Object.assign({}, c.s, style, { border: (c.s.border || '') + ((style && style.border) || '') })
    return this
  }
  range(ref, fn) {
    const [a, b] = ref.split(':')
    const s = decode(a)
    const e = decode(b || a)
    for (let r = s.r; r <= e.r; r++) {
      for (let c = s.c; c <= e.c; c++) fn(colName(c) + (r + 1), r === s.r, r === e.r, c === s.c, c === e.c)
    }
  }
  merge(ref) { this.merges.push(ref); return this }
  // 범위 바깥 테두리
  box(ref) {
    this.range(ref, (addr, top, bottom, left, right) => {
      const c = this.cell(addr)
      c.s = Object.assign({}, c.s, { border: (c.s.border || '') + (left ? 'L' : '') + (right ? 'R' : '') + (top ? 'T' : '') + (bottom ? 'B' : '') })
    })
    return this
  }
  // 범위 안 모든 칸 테두리(표)
  grid(ref, style) {
    this.range(ref, addr => this.put(addr, undefined, Object.assign({}, style || {}, { border: 'LRTB' })))
    return this
  }
  width(col, w) { this.cols[col] = w; return this }
  height(row, h) { this.heights[row] = h; return this }
  xml(styles, drawingRid) {
    const rows = {}
    let maxR = 0
    let maxC = 0
    for (const addr of Object.keys(this.cells)) {
      const { r, c } = decode(addr)
      if (!rows[r]) rows[r] = []
      rows[r].push({ addr, c, cell: this.cells[addr] })
      maxR = Math.max(maxR, r)
      maxC = Math.max(maxC, c)
    }
    Object.keys(this.heights).forEach(r => { maxR = Math.max(maxR, +r - 1) })
    const out = []
    for (let r = 0; r <= maxR; r++) {
      const list = (rows[r] || []).sort((a, b) => a.c - b.c)
      const ht = this.heights[r + 1]
      if (!list.length && !ht) continue
      out.push(`<row r="${r + 1}"${ht ? ` ht="${ht}" customHeight="1"` : ''}>`)
      for (const { addr, cell } of list) {
        const s = styles.id(cell.s)
        const sa = s ? ` s="${s}"` : ''
        if (cell.f) {
          out.push(`<c r="${addr}"${sa}><f>${esc(cell.f)}</f>${typeof cell.v === 'number' ? `<v>${cell.v}</v>` : ''}</c>`)
        } else if (typeof cell.v === 'number') {
          out.push(`<c r="${addr}"${sa}><v>${cell.v}</v></c>`)
        } else if (cell.v !== undefined) {
          out.push(`<c r="${addr}"${sa} t="inlineStr"><is><t xml:space="preserve">${esc(cell.v)}</t></is></c>`)
        } else {
          out.push(`<c r="${addr}"${sa}/>`)
        }
      }
      out.push('</row>')
    }
    const cols = Object.keys(this.cols).map(k => decode(k + '1').c).sort((a, b) => a - b)
      .map(c => `<col min="${c + 1}" max="${c + 1}" width="${this.cols[colName(c)]}" customWidth="1"/>`).join('')
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
      '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
      `<dimension ref="A1:${colName(maxC)}${maxR + 1}"/>` +
      '<sheetViews><sheetView workbookViewId="0"/></sheetViews>' +
      '<sheetFormatPr defaultRowHeight="16.5"/>' +
      (cols ? `<cols>${cols}</cols>` : '') +
      `<sheetData>${out.join('')}</sheetData>` +
      (this.merges.length ? `<mergeCells count="${this.merges.length}">${this.merges.map(m => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>` : '') +
      '<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>' +
      '<pageSetup paperSize="9" orientation="portrait"/>' +
      (drawingRid ? `<drawing r:id="${drawingRid}"/>` : '') +
      '</worksheet>'
  }
}

// 라벨(굵게 9pt) / 값(10pt) — 샘플 머리 블록 서식
const LABEL = { font: { sz: 9, b: true }, h: 'left', v: 'center' }
const VALUE_B = { font: { sz: 10, b: true }, h: 'left', v: 'center' }
const VALUE = { font: { sz: 10 }, h: 'left', v: 'center' }
const TH = { font: { sz: 9, b: true }, h: 'center', v: 'center', wrap: true }
const TD = { font: { sz: 11 }, h: 'center', v: 'center', wrap: true }
const USD = '"$"#,##0.00;\\-"$"#,##0.00'
const DATE_FMT = 'yyyy-mm-dd'

// 수화인(For Account & Risk of Messrs) 칸 — 吴亚欣 양식(2026-10-16 #44): 회사명 한 줄 + 「주소 E-MAIL : … TEL: …」 한 덩어리(칸을 합쳐 자동 줄바꿈)
// 양식 글: 「Room 2301 … Republic of Korea E-MAIL : A,B,C  TEL: 82-10-…」(TEL 앞 공백 둘). 주소 두 줄(addr1·addr2)은 공백 하나로 잇는다
export function importerBlock(im) {
  const i = im || {}
  const addr = [i.addr1, i.addr2].map(v => text(v)).filter(Boolean).join(' ')
  const mail = text(i.email) ? 'E-MAIL : ' + text(i.email) : ''
  const tel = text(i.tel) ? 'TEL: ' + text(i.tel) : ''
  return { name: text(i.name), body: [addr, mail].filter(Boolean).join(' ') + (tel ? (addr || mail ? '  ' : '') + tel : '') }
}
// IV 품목 줄 — 파렛트를 넘어 (영어품명, HS 전체 자리, 단가) 가 모두 같으면 한 줄로 합친다(2026-10-07 YZY 「相同HS编码」). 단가가 다르면 따로 둔다.
// HS 는 숫자만 남겨 전체 자리로 비교한다 — 뒷자리 다른 HS 를 합치면 통관 신고 품목이 바뀐다(产地证 의 6자리 합치기와 다르다).
// 품명은 대소문자·공백 차이를 무시하고 비교, 표시는 첫 등장 표기 그대로. 순서는 产地证(cooItems)과 같은 첫 등장 순, HS 칸도 첫 줄 값. 수량·금액만 더한다
function hsKeyFull(hs) { return text(hs).replace(/\D/g, '') }
export function ivItems(model) {
  const map = new Map()
  for (const p of (model && model.pallets) || []) {
    for (const it of p.items) {
      const key = text(it.desc).replace(/\s+/g, ' ').toLowerCase() + '|' + hsKeyFull(it.hs) + '|' + it.price + (it.fixed ? '|F' : '')
      if (!map.has(key)) map.set(key, Object.assign({ hs: it.hs, desc: it.desc, price: it.price, qty: 0, amount: 0, url: '', img: '' }, it.fixed ? { fixed: true } : {}))
      const m = map.get(key)
      m.qty += it.qty
      m.amount += it.amount
      if (!m.url) m.url = it.url
      if (!m.img) m.img = it.img
    }
  }
  return [...map.values()].map(m => Object.assign(m, { amount: round(m.amount, 4) }))
}

// IV Mark 칸 글 — 托盘号(散货 는 箱号)를 나온 순서대로, 같은 머리 + 끝 번호가 1씩 이어지면 「첫~끝」 한 줄로,
// 끊기거나 머리가 바뀌면 새 줄(2026-10-07 YZY 「RUBY1007-45~RUBY1007-56」「NUBE100602 ~ NUBE100604」). 箱号 범위는 boxMarkText 처럼 「 ~ 」
export function ivMarkText(pallets) {
  const tokens = []
  const seen = new Set()
  ;(pallets || []).forEach(p => {
    const loose = !!(p.kind === 'BOX' && p.boxCodes && p.boxCodes.length)
    const list = loose ? [].concat(...p.boxCodes.map(c => String(c).split(/,\s*/))) : [p.id]
    list.map(text).filter(Boolean).forEach(t => {
      if (seen.has(t)) return
      seen.add(t)
      tokens.push({ t, box: loose })
    })
  })
  const out = []
  let cur = null
  const flush = () => { if (cur) out.push(cur.first === cur.last ? cur.first : cur.first + (cur.box ? ' ~ ' : '~') + cur.last) }
  tokens.forEach(({ t, box }) => {
    const m = /^(.*?)(\d+)$/.exec(t)
    const n = m ? parseInt(m[2], 10) : NaN
    if (cur && m && cur.head === m[1] && cur.box === box && n === cur.n + 1) {
      cur.last = t
      cur.n = n
      return
    }
    flush()
    cur = { head: m ? m[1] : null, n, box, first: t, last: t }
  })
  flush()
  return out.join('\n')
}

// coRefOf(item) → 「CO1--1」(产地证 몇 번째 장--그 장의 몇 번째 행). 产地证 을 아직 안 만들었으면 없다 → H 열 빈칸(2026-10-04 YZY)
function buildIV(model, info, coRefOf) {
  const sh = new Sheet('IV')
  ;[['A', 18.7], ['B', 9], ['C', 15.8], ['D', 23.8], ['E', 11.6], ['F', 9.9], ['G', 17.1], ['H', 9], ['I', 30], ['J', 30]]
    .forEach(([c, w]) => sh.width(c, w))
  sh.height(3, 13.5).height(15, 17.25)
  const serial = toSerial(info.date)

  sh.put('A1', 'COMMERCIAL INVOICE', { font: { sz: 16, b: true }, h: 'center', v: 'center' }).merge('A1:G2')
  sh.range('A2:G2', a => sh.put(a, undefined, { border: 'B' }))

  // 수출자 / 날짜
  sh.put('A3', ' Shipper/Exporter', LABEL).merge('A3:D3')
  sh.put('A4', info.exporter.name, VALUE_B).merge('A4:D4')
  sh.put('A5', info.exporter.addr1, VALUE_B).merge('A5:D5')
  sh.put('A6', info.exporter.addr2, VALUE_B).merge('A6:D6')
  sh.merge('A7:D7').merge('A8:D8').box('A3:D8')
  sh.put('E3', 'NO. & Date of Invoice', Object.assign({}, LABEL, { h: 'center', wrap: true })).merge('E3:G3')
  sh.put('E4', serial === null ? info.date : serial, { font: { sz: 9, b: true }, h: 'center', v: 'center', fmt: DATE_FMT }).merge('E4:G4')
  sh.box('E3:G4').box('E5:G6')
  sh.put('E7', ' L/C No. & Date', LABEL).merge('E7:G7').merge('E8:G8').box('E7:G8')

  // 수입자
  sh.put('A9', ' For Account & Risk of Messrs', LABEL).merge('A9:D9')
  // 양식(箱단발票模版): A10 = 회사명, A11:D14 = 주소·E-MAIL·TEL 한 덩어리(2026-10-16 吴亚欣 #44)
  {
    const b = importerBlock(info.importer)
    sh.put('A10', b.name, VALUE_B).merge('A10:D10')
    sh.put('A11', b.body, Object.assign({}, VALUE_B, { wrap: true, v: 'top' })).merge('A11:D14')
  }
  sh.box('A9:D14')
  sh.put('E9', ' L/C Issuing Bank', LABEL).merge('E9:G9').merge('E10:G10').box('E9:G10')
  sh.put('E11', ' Remarks:', LABEL).merge('E11:G11')
  sh.put('E14', info.remarks || '', { font: { sz: 10 }, h: 'center', v: 'center', wrap: true }).merge('E14:G22').box('E11:G22')

  sh.put('A15', ' Notify Party', LABEL).merge('A15:D15')
  sh.put('A16', info.notify || '', VALUE).merge('A16:D16').merge('A17:D17').merge('A18:D18').box('A15:D18')

  sh.put('A19', ' Port of Loading', LABEL).merge('A19:B19')
  sh.put('A20', info.portLoading, VALUE).merge('A20:B20').box('A19:B20')
  sh.put('D19', ' Final Destination', LABEL)
  sh.put('D20', info.finalDest, VALUE).box('C19:D20')
  sh.put('A21', ' Carrier', LABEL).merge('A21:B21')
  sh.put('A22', info.carrier || '', VALUE).merge('A22:B22').box('A21:B22')
  sh.put('D21', ' Sailing on/or About', LABEL)
  sh.put('D22', info.sailing || '', VALUE).box('C21:D22')

  // 품목 표
  const head = { A: ' Mark & Number of', C: 'HSCODE', D: 'Description of Goods', E: 'Quantity(PCS)', F: 'Unit Price', G: 'Amount', H: 'CO', I: 'URL', J: 'Image' }
  sh.grid('A23:J23', TH)
  Object.keys(head).forEach(c => sh.put(c + '23', head[c], c === 'A' ? Object.assign({}, TH, { h: 'left' }) : TH))
  sh.merge('A23:B23')
  let r = 24
  let totalQty = 0
  let totalAmt = 0
  const start = r
  ivItems(model).forEach(it => {
    sh.height(r, 45)
    sh.grid('A' + r + ':J' + r, TD)
    if (it.fixed) sh.range('C' + r + ':J' + r, addr => sh.put(addr, undefined, { fill: 'FFFFFF00' })) // 고정 申报金额 쓴 줄 = 노란색(2026-10-11 YZY)
    sh.put('C' + r, it.hs).put('D' + r, it.desc).put('E' + r, it.qty)
    sh.put('F' + r, it.price, { font: { sz: 11, b: true }, fmt: USD })
    sh.put('G' + r, it.amount, { font: { sz: 11, b: true }, fmt: USD })
    if (coRefOf) sh.put('H' + r, coRefOf(it) || undefined)
    sh.put('I' + r, it.url, { font: { sz: 9 } }).put('J' + r, it.img, { font: { sz: 9 } })
    totalQty += it.qty
    totalAmt += it.amount
    r += 1
  })
  // Mark & Number = 품목 줄 전체를 세로로 합친 칸 하나(머리처럼 A:B), 托盘号·箱号 범위를 줄마다(2026-10-07 YZY)
  if (r > start) {
    const mark = ivMarkText(model.pallets)
    sh.put('A' + start, mark, { fmt: '@' }).merge('A' + start + ':B' + (r - 1))
    // 범위 줄이 많아 칸에 안 들어가면 행 높이를 나눠 늘린다(한 줄 ≈ 15pt)
    const per = Math.ceil(mark.split('\n').length * 15 / (r - start))
    if (per > 45) for (let k = start; k < r; k++) sh.height(k, per)
  }
  // 샘플처럼 빈 줄 두 개 뒤 TOTAL
  sh.grid('A' + r + ':G' + (r + 1), TD)
  sh.merge('B' + (r + 1) + ':D' + (r + 1))
  const t = r + 2
  sh.grid('A' + t + ':G' + t, TD)
  sh.put('B' + t, 'TOTAL').merge('B' + t + ':D' + t)
  sh.formula('E' + t, 'SUM(E24:E' + (t - 1) + ')', totalQty, { font: { sz: 11, b: true }, fmt: '#,##0_);[Red](#,##0)' })
  sh.formula('G' + t, 'SUM(G24:G' + (t - 1) + ')', round(totalAmt, 4), { font: { sz: 11, b: true }, fmt: USD })
  return sh
}

function buildPA(model, info) {
  const sh = new Sheet('PA')
  ;[['A', 29], ['B', 45.6], ['C', 9], ['D', 9.7], ['E', 12.6], ['F', 15.2]].forEach(([c, w]) => sh.width(c, w))
  sh.height(1, 22.5).height(2, 22.5)
  const serial = toSerial(info.date)

  sh.put('A1', 'PACKING LIST', { font: { sz: 16, b: true }, h: 'center', v: 'center' }).merge('A1:E1')
  sh.range('A2:E2', a => sh.put(a, undefined, { border: 'B' }))

  sh.put('A3', ' Shipper/Exporter', LABEL)
  sh.put('A4', info.exporter.name, VALUE_B).merge('A4:B4')
  sh.put('A5', info.exporter.addr1, Object.assign({}, VALUE_B, { wrap: true })).merge('A5:B5')
  sh.put('A6', info.exporter.addr2, Object.assign({}, VALUE_B, { wrap: true })).merge('A6:B6')
  sh.box('A3:B6')
  sh.merge('C3:E4').box('C3:E4')
  sh.put('C5', ' L/C No. & Date', VALUE_B)
  sh.put('C6', serial === null ? info.date : serial, { font: { sz: 9, b: true }, h: 'center', v: 'center', fmt: DATE_FMT }).merge('C6:E6')
  sh.box('C5:E6')

  sh.put('A7', ' For Account & Risk of Messrs', LABEL)
  {
    const b = importerBlock(info.importer)
    sh.put('A8', b.name, VALUE_B).merge('A8:B8')
    sh.put('A9', b.body, Object.assign({}, VALUE_B, { wrap: true, v: 'top' })).merge('A9:B12')
  }
  sh.box('A7:B12')
  sh.put('C7', ' Remarks:', LABEL)
  if (info.remarks) sh.put('C8', info.remarks, { font: { sz: 10 }, h: 'center', v: 'center', wrap: true }).merge('C8:E12')
  sh.box('C7:E20')

  sh.put('A13', ' Notify Party', LABEL)
  if (info.notify) sh.put('A14', info.notify, VALUE)
  sh.box('A13:B16')
  sh.put('A17', ' Port of Loading', LABEL).put('A18', info.portLoading, VALUE).box('A17:A18')
  sh.put('B17', ' Final Destination', LABEL).put('B18', info.finalDest, VALUE).box('B17:B18')
  sh.put('A19', ' Carrier', LABEL).put('A20', info.carrier || '', VALUE).box('A19:A20')
  sh.put('B19', ' Sailing on/or About', LABEL).put('B20', info.sailing || '', VALUE).box('B19:B20')

  sh.grid('A21:E21', TH)
  sh.put('A21', ' Mark & Number of', Object.assign({}, TH, { h: 'left' }))
    .put('B21', 'Description of Goods').put('C21', 'Quantity').put('D21', "G/w't(Kg)").put('E21', 'Measur(CBM)')
  let r = 22
  let gw = 0
  let cbm = 0
  // 한 묶음(托盘 하나, 또는 散货 박스 하나) = Mark·毛重·体积·尺寸 을 세로로 합친 줄들.
  // c === null 이면 体积 을 이 묶음에 안 쓴다(散货 는 묶음 전체를 아래에서 한 칸으로 합친다)
  const block = (mark, items, g, c, dims, mergeGw) => {
    const start = r
    items.forEach(it => {
      sh.height(r, 33)
      sh.grid('A' + r + ':F' + r, TD)
      sh.put('B' + r, it.desc).put('C' + r, it.qty)
      r += 1
    })
    if (r === start) return
    const end = r - 1
    sh.put('A' + start, mark, { fmt: '@' })
    if (!mergeGw) sh.put('D' + start, num(g) || undefined) // 毛重 0 = 모름 → 빈칸(2026-10-06 YZY). mergeGw = 같은 고객 박스끼리 아래에서 한 칸으로 합친다
    if (c !== null) sh.put('E' + start, num(c))
    sh.put('F' + start, dims || '', { fmt: '@' })
    if (end > start) (c !== null ? ['A', 'D', 'E', 'F'] : ['A', 'D', 'F']).filter(col => !(mergeGw && col === 'D')).forEach(col => sh.merge(col + start + ':' + col + end))
    gw += num(g)
    if (c !== null) cbm += num(c)
  }
  model.pallets.forEach(p => {
    // 散货 는 箱号 마다 한 줄, Measur(CBM) 은 그 箱号 줄들을 합친 한 칸에 총 방수(2026-10-04 YZY). 托盘号 있는 건 예전처럼 托盘 단위
    if (hasBoxRows(p)) {
      const start = r
      // 散货 毛重(G/W) — 같은 고객(화주) 박스가 이어지면 그 줄들을 세로로 합친 한 칸에 합계를 적는다(2026-10-16 吴亚欣 #29). 整托 는 그대로
      const runs = []
      p.boxes.forEach(b => {
        const last = runs[runs.length - 1]
        if (last && b.co && last.co === b.co) last.list.push(b)
        else runs.push({ co: b.co, list: [b] })
      })
      runs.forEach(run => {
        const rs = r
        const mergeGw = run.list.length > 1
        run.list.forEach(b => block(b.code, b.items, b.gw, null, b.dims, mergeGw))
        if (mergeGw && r - 1 > rs) {
          sh.put('D' + rs, num(round(run.list.reduce((a, b) => a + num(b.gw), 0), 2)) || undefined)
          sh.merge('D' + rs + ':D' + (r - 1))
        }
      })
      if (r === start) return
      sh.put('E' + start, num(p.cbm))
      if (r - 1 > start) sh.merge('E' + start + ':E' + (r - 1))
      cbm += num(p.cbm)
    } else block(palletMark(p), p.items, p.gw, p.cbm, p.dims)
  })
  sh.grid('A' + r + ':E' + r, TD)
  const t = r + 1
  sh.grid('A' + t + ':E' + t, { font: { sz: 10, b: true }, h: 'center', v: 'center' })
  sh.put('B' + t, 'TOTAL:').put('C' + t, packageCountText(model.pallets))
  sh.formula('D' + t, 'SUM(D22:D' + (t - 1) + ')', round(gw, 4), { fmt: '0.00_);[Red]\\(0.00\\)' })
  sh.formula('E' + t, 'SUM(E22:E' + (t - 1) + ')', round(cbm, 4), { fmt: '0.00_);[Red]\\(0.00\\)' })
  return sh
}

// 그림 위치(샘플과 같은 자리): 도장 — IV D3 근처 / PA B2 근처, 사업자등록증 — Sheet1 A1
const SEAL_IV = { col: 3, colOff: 410210, row: 2, rowOff: 142875, cx: 817880, cy: 851535 }
const SEAL_PA = { col: 1, colOff: 2658745, row: 1, rowOff: 282575, cx: 817880, cy: 851535 }

function imageExt(img) {
  return /jpe?g/i.test(img.type || '') ? 'jpeg' : 'png'
}
// 원본 비율을 지키며 상자(cx·cy) 안에 맞춘다(가로·세로 px 를 알 때만)
export function fitBox(img, box) {
  if (!img.width || !img.height) return box
  const k = Math.min(box.cx / (img.width * EMU_PER_PX), box.cy / (img.height * EMU_PER_PX))
  return Object.assign({}, box, { cx: Math.round(img.width * EMU_PER_PX * k), cy: Math.round(img.height * EMU_PER_PX * k) })
}
function drawingXml(anchors) {
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    anchors.map((a, i) =>
      `<xdr:oneCellAnchor><xdr:from><xdr:col>${a.col}</xdr:col><xdr:colOff>${a.colOff}</xdr:colOff><xdr:row>${a.row}</xdr:row><xdr:rowOff>${a.rowOff}</xdr:rowOff></xdr:from>` +
      `<xdr:ext cx="${a.cx}" cy="${a.cy}"/>` +
      `<xdr:pic><xdr:nvPicPr><xdr:cNvPr id="${i + 2}" name="Picture ${i + 1}"/><xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr>` +
      `<xdr:blipFill><a:blip r:embed="rId${i + 1}"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill>` +
      `<xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${a.cx}" cy="${a.cy}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic><xdr:clientData/></xdr:oneCellAnchor>`
    ).join('') + '</xdr:wsDr>'
}

function rels(list) {
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
    list.map(r => `<Relationship Id="${r.id}" Type="${r.type}" Target="${r.target}"/>`).join('') + '</Relationships>'
}
const REL = {
  doc: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument',
  sheet: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet',
  styles: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles',
  drawing: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing',
  image: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/image'
}

/**
 * 인보이스 엑셀(Uint8Array). images = { seal: {data:Uint8Array, type, width, height}, license: {...} } (둘 다 선택)
 * info = { date:'YYYY-MM-DD', exporter:{name,addr1,addr2}, importer:{name,addr1,addr2,email,tel},
 *          portLoading, finalDest, carrier, sailing, notify, remarks }
 */
export function writeInvoiceXlsx(model, info, images, coRefOf) {
  const imgs = images || {}
  const styles = new Styles()
  const sheets = []
  if (imgs.license && imgs.license.data) {
    const s1 = new Sheet('Sheet1')
    s1.images.push({ img: imgs.license, anchor: fitBox(imgs.license, { col: 0, colOff: 9525, row: 0, rowOff: 9525, cx: 5980430, cy: 9519285 }) })
    sheets.push(s1)
  }
  const iv = buildIV(model, info, coRefOf)
  const pa = buildPA(model, info)
  if (imgs.seal && imgs.seal.data) {
    iv.images.push({ img: imgs.seal, anchor: fitBox(imgs.seal, SEAL_IV) })
    pa.images.push({ img: imgs.seal, anchor: fitBox(imgs.seal, SEAL_PA) })
  }
  sheets.push(iv, pa)
  return packXlsx(sheets, styles, sheets.indexOf(iv))
}

/**
 * 시트들을 xlsx(zip) 한 파일로 묶는다(Uint8Array). 원산지증명(cooXlsx.js)도 이걸 쓴다.
 * @param {Sheet[]} sheets
 * @param {Styles} styles
 * @param {number} activeTab 열었을 때 보이는 시트
 */
export function packXlsx(sheets, styles, activeTab) {
  const enc = new TextEncoder()
  const files = {}
  const media = []
  let drawingNo = 0
  const overrides = []
  sheets.forEach((sh, i) => {
    const n = i + 1
    let rid = ''
    if (sh.images.length) {
      drawingNo += 1
      rid = 'rId1'
      const dRels = sh.images.map((im, k) => {
        let m = media.find(x => x.img === im.img)
        if (!m) {
          m = { img: im.img, name: 'image' + (media.length + 1) + '.' + imageExt(im.img) }
          media.push(m)
        }
        return { id: 'rId' + (k + 1), type: REL.image, target: '../media/' + m.name }
      })
      files['xl/drawings/drawing' + drawingNo + '.xml'] = drawingXml(sh.images.map(im => im.anchor))
      files['xl/drawings/_rels/drawing' + drawingNo + '.xml.rels'] = rels(dRels)
      files['xl/worksheets/_rels/sheet' + n + '.xml.rels'] = rels([{ id: 'rId1', type: REL.drawing, target: '../drawings/drawing' + drawingNo + '.xml' }])
      overrides.push(`<Override PartName="/xl/drawings/drawing${drawingNo}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`)
    }
    files['xl/worksheets/sheet' + n + '.xml'] = sh.xml(styles, rid)
    overrides.push(`<Override PartName="/xl/worksheets/sheet${n}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
  })
  files['xl/workbook.xml'] = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<bookViews><workbookView activeTab="${activeTab}"/></bookViews><sheets>` +
    sheets.map((sh, i) => `<sheet name="${esc(sh.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') +
    '</sheets><calcPr calcId="191029" fullCalcOnLoad="1"/></workbook>'
  files['xl/_rels/workbook.xml.rels'] = rels(sheets.map((sh, i) => ({ id: 'rId' + (i + 1), type: REL.sheet, target: 'worksheets/sheet' + (i + 1) + '.xml' }))
    .concat([{ id: 'rId' + (sheets.length + 1), type: REL.styles, target: 'styles.xml' }]))
  files['xl/styles.xml'] = styles.xml()
  files['_rels/.rels'] = rels([{ id: 'rId1', type: REL.doc, target: 'xl/workbook.xml' }])
  files['[Content_Types].xml'] = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' +
    '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
    '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
    '<Default Extension="xml" ContentType="application/xml"/>' +
    '<Default Extension="png" ContentType="image/png"/>' +
    '<Default Extension="jpeg" ContentType="image/jpeg"/>' +
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
    overrides.join('') + '</Types>'

  const cfb = XLSX.CFB.utils.cfb_new()
  // [Content_Types].xml 이 zip 맨 앞에 와야 하는 프로그램이 있어 먼저 넣는다
  const order = ['[Content_Types].xml'].concat(Object.keys(files).filter(k => k !== '[Content_Types].xml'))
  order.forEach(k => XLSX.CFB.utils.cfb_add(cfb, '/' + k, enc.encode(files[k])))
  media.forEach(m => XLSX.CFB.utils.cfb_add(cfb, '/xl/media/' + m.name, m.img.data))
  const out = XLSX.CFB.write(cfb, { fileType: 'zip', type: 'array', compression: true })
  return out instanceof Uint8Array ? out : new Uint8Array(out)
}

export default { extraCols, parsePalletSpec, listPallets, buildInvoiceModel, packageCountText, invoiceFileName, writeInvoiceXlsx, packXlsx, ivItems, ivMarkText }
